import React, { useEffect, useRef, useState } from 'react'
import { FaReply, FaPaperclip, FaPaperPlane, FaTimes } from "react-icons/fa";
import { MwInputFile, MwSpinnerButton, MwTextArea } from '../ui';
import { useCurrent, usePost } from '../../hooks';
import { useTranslation } from 'react-i18next';
import { apiClient as axios, buildAuthRequestConfig } from '../../utils/apiClient';

const LARGE_UPLOAD_REQUEST_CONFIG = {
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
    timeout: 0,
};

const stripHtmlText = (value) =>
    String(value || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const CreateTicketDetails = ({ ticketID, callBackOnCreated }) => {
    const [t] = useTranslation('global')
    const { user } = useCurrent();
    const { data: resultSubmit, loading: loadingSubmit, postData: submitTicket } = usePost(false);
    const [fileLoading, setFileLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [content, setContent] = useState('');
    const [attchment, setAttchment] = useState([]);
    const [errors, setErrors] = useState({});
    const handledSubmitRef = useRef(null);
    const fileInputId = 'support_ticket_details_attachment';

    const getAttachmentName = (url) => {
        if (!url) return '';
        try {
            const orig = String(url).split('/').pop() || '';
            const cleaned = orig.replace(/^\d+-[a-f0-9-]+-/, '');
            const parts = cleaned.split('.');
            if (parts.length <= 1) return cleaned || orig;
            const last = parts.pop();
            if (last && !isNaN(last) && parts.length > 0) return parts.join('.');
            return cleaned || orig;
        } catch {
            return String(url);
        }
    };

    const getUploadedLocations = (payload) => {
        const values = [
            payload?.location,
            payload?.data?.location,
            payload?.locations,
            payload?.data?.locations,
            payload?.files,
            payload?.data?.files,
        ];
        return values
            .flatMap((value) => Array.isArray(value) ? value : [value])
            .map((value) => typeof value === 'string' ? value : (value?.location || value?.url || value?.path || ''))
            .filter(Boolean);
    };

    const uploadHandler = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setFileLoading(true);
        setErrors((state) => ({ ...state, attachment: undefined }));
        try {
            const uploadedLocations = [];
            for (const file of files) {
                const _formData = new FormData();
                _formData.append('myfile', file, file.name);
                const response = await axios.post(
                    `${process.env.REACT_APP_UPLOADS_API_BASE_URL}/support`,
                    _formData,
                    buildAuthRequestConfig(LARGE_UPLOAD_REQUEST_CONFIG)
                );
                uploadedLocations.push(...getUploadedLocations(response.data));
            }
            setAttchment((state) => [...(Array.isArray(state) ? state : state ? [state] : []), ...uploadedLocations]);
            e.target.value = '';
        } catch {
            setErrors((state) => ({ ...state, attachment: t('support.tickets.uploadFailed') }));
        } finally {
            setFileLoading(false);
        }
    }

    const removeAttachment = async (attachmentUrl) => {
        if (!attachmentUrl) return;
        setDeleteLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.delete(
                `${process.env.REACT_APP_UPLOADS_API_BASE_URL}/delete`,
                {
                    data: { keyFile: attachmentUrl },
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                    validateStatus: () => true
                }
            );
            if (response?.status >= 200 && response?.status < 300) {
                setAttchment((state) => (Array.isArray(state) ? state : [state]).filter((url) => url !== attachmentUrl));
                setErrors((state) => ({ ...state, attachment: undefined }));
                const input = document.getElementById(fileInputId);
                if (input) {
                    input.value = '';
                }
                return;
            }
            setErrors((state) => ({ ...state, attachment: t('support.tickets.uploadFailed') }));
        } catch {
            setErrors((state) => ({ ...state, attachment: t('support.tickets.uploadFailed') }));
        } finally {
            setDeleteLoading(false);
        }
    };



    const submitHandler = () => {
        setErrors({});
        const contentText = stripHtmlText(content);
        if (!contentText && !attchment.length) {
            setErrors((state) => ({ ...state, content: t('support.tickets.commentRequired') }));
            return;
        }
        if (fileLoading) {
            setErrors((state) => ({ ...state, attachment: t('support.tickets.uploadInProgress') }));
            return;
        }
        const persistedUserId = (() => {
            try {
                const persistedUser = JSON.parse(localStorage.getItem('user') || 'null');
                return (
                    persistedUser?._id ||
                    persistedUser?.id ||
                    persistedUser?.userId ||
                    persistedUser?.user?._id ||
                    persistedUser?.user?.id ||
                    persistedUser?.user?.userId ||
                    null
                );
            } catch {
                return null
            }
        })();
        const tokenUserId = (() => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return null;
                const parts = token.split('.');
                if (parts.length < 2) return null;
                const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
                const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
                const json = atob(padded);
                const data = JSON.parse(json);
                return data?._id || data?.id || data?.userId || data?.sub || null;
            } catch {
                return null;
            }
        })();

        const createdById =
            user?._id ||
            user?.id ||
            user?.userId ||
            user?.user?._id ||
            user?.user?.id ||
            user?.user?.userId ||
            persistedUserId ||
            tokenUserId;

        if (!createdById) {
            window.alert('تعذر تحديد المستخدم الحالي. الرجاء تسجيل الدخول مرة أخرى.');
            return;
        }
        const _data =
        {
            ticketId: ticketID,
            content: content || null,
            attachments: attchment.length ? attchment : null,
            createdBy: createdById,
            visibility: 'external'
        }
        handledSubmitRef.current = null;
        submitTicket(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/details`, _data);
    }
    useEffect(() => {
        const submitKey = resultSubmit?.data?._id || resultSubmit?.message || null;
        if (resultSubmit && submitKey && handledSubmitRef.current !== submitKey) {
            handledSubmitRef.current = submitKey;
            setContent('');
            setAttchment([]);
            callBackOnCreated();
        }
    }, [callBackOnCreated, resultSubmit]);

    return (
        <div className='bg-white p-6 my-6 rounded-2xl shadow-sm border border-slate-100'>
            <div className='pb-4 text-sm font-bold text-slate-700 flex items-center gap-2'>
                <div className="p-1.5 bg-blue-50 text-blue-600 rounded-lg">
                    <FaReply />
                </div>
                {t('support.tickets.addComment')}
            </div>

            <div className="relative">
                <MwTextArea
                    rows={4}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    invalid={!!errors?.content}
                    invalidMsg={errors?.content}
                    classNameCustom="rounded-xl border-slate-200 focus:border-[#3D3B40] transition-all resize-none p-4"
                    placeholder={t('support.tickets.typeYourReply')}
                    dir="auto"
                    rich
                />
            </div>

            <div className='flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mt-4'>
                <div className="flex-1 w-full md:w-auto">
                    <MwInputFile
                        label={
                            <div className="flex items-center gap-2 text-slate-500 hover:text-[#3D3B40] transition-colors cursor-pointer group">
                                <FaPaperclip className="group-hover:rotate-45 transition-transform" />
                                <span>{attchment.length ? `${t('support.tickets.attachments')} (${attchment.length})` : t('support.tickets.attachFile')}</span>
                            </div>
                        }
                        onChangeHandler={uploadHandler}
                        acceptTypes='*/*'
                        id={fileInputId}
                        loading={fileLoading}
                        disabled={fileLoading || deleteLoading}
                        classNameCustom="w-full justify-start"
                        classNameInput="sr-only"
                        multiple
                    />
                    {attchment.map((attachmentUrl) => (
                        <div key={attachmentUrl} className="mt-2 flex items-center justify-between gap-3">
                            <a
                                href={attachmentUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[11px] font-semibold text-slate-600 hover:text-[#3D3B40] truncate"
                            >
                                {getAttachmentName(attachmentUrl)}
                            </a>
                            <button
                                type="button"
                                onClick={() => removeAttachment(attachmentUrl)}
                                disabled={fileLoading || deleteLoading}
                                className="p-1 hover:bg-red-50 rounded-full text-red-500 hover:text-red-700 transition-colors disabled:opacity-50"
                            >
                                <FaTimes size={14} />
                            </button>
                        </div>
                    ))}
                    {!!attchment.length && (
                        <div className="mt-2 text-[10px] text-green-600 font-bold flex items-center gap-1">
                            <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                            {t('support.tickets.fileUploadedSuccessfully')}
                        </div>
                    )}
                    {errors?.attachment && <div className="mt-2 text-[11px] text-red-500 font-medium">{errors.attachment}</div>}
                    {!errors?.attachment && fileLoading && <div className="mt-2 text-[11px] text-slate-500 font-medium">{t('support.tickets.uploadInProgress')}</div>}
                </div>

                <div className="w-full md:w-auto flex justify-end">
                    {loadingSubmit ? (
                        <MwSpinnerButton />
                    ) : (
                        <button
                            onClick={submitHandler}
                            disabled={(!stripHtmlText(content) && !attchment.length) || fileLoading}
                            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold transition-all shadow-sm ${((!stripHtmlText(content) && !attchment.length) || fileLoading) ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'primary-bg-color text-white hover:opacity-90 hover:shadow-md active:scale-95'}`}
                        >
                            <span>{t('support.tickets.sendComment')}</span>
                            <FaPaperPlane className="text-xs" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}

export default CreateTicketDetails
